import React, { useEffect, useMemo, useState } from 'react';
import Layout from '../../components/Layout';
import Button from '../../components/Button';
import ReportToolbar from '../../shared/ReportToolbar';
import { computePeriodRange } from '../../shared/PeriodSelect';
import { waybillAPI } from '../../api/waybillAPI';
import useGuardedMutation from '../../hooks/useGuardedMutation';

// Severity is capped at 3 colors per DESIGN.MD (§5.1/§9): 0–7 and 8–15 days are
// still within a normal window and share the same quiet neutral treatment —
// only 16–30 (warning) and 31+ (danger) actually need a distinct signal.
const BUCKET_STYLES = {
  '0-7':   'bg-ink-canvas text-ink-body border-ink-line',
  '8-15':  'bg-ink-canvas text-ink-body border-ink-line',
  '16-30': 'bg-warning-50 text-warning-700 border-warning-200',
  '31+':   'bg-danger-50 text-danger-700 border-danger-200',
};

const BUCKET_LABELS = {
  '0-7':   '0–7 days',
  '8-15':  '8–15 days',
  '16-30': '16–30 days',
  '31+':   '31+ days',
};

const emptyData = { rows: [], agingSummary: [], pullOutAgingSummary: [], noPullOutGroup: { count: 0, amount: 0 }, totalCount: 0 };

const PULLOUT_STATUS_LABELS = { all: 'All', with: 'With Pull-Out Date', without: 'Without Pull-Out Date' };

const PHP = (n) =>
  new Intl.NumberFormat('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0);

const PRESET_LABELS = {
  THIS_MONTH:   'This Month',
  LAST_MONTH:   'Last Month',
  THIS_QUARTER: 'This Quarter',
  YTD:          'Year to Date',
  FY:           'Fiscal Year',
};

const formatDate = (value) => {
  if (!value) return '—';
  return new Date(value).toLocaleDateString('en-PH', {
    month: 'numeric',
    day: 'numeric',
    year: '2-digit',
  });
};

const formatLongDate = (dateStr) => {
  if (!dateStr) return '';
  return new Date(`${dateStr}T00:00:00`).toLocaleDateString('en-PH', {
    month: 'long', day: 'numeric', year: 'numeric',
  });
};

// "Year to Date (Jan 1, 2026 – Jul 27, 2026)" for named presets, plain range for Custom.
function periodDisplayLabel(period) {
  const rangeLabel = period.startDate && period.endDate
    ? (period.startDate === period.endDate
        ? formatLongDate(period.startDate)
        : `${formatLongDate(period.startDate)} – ${formatLongDate(period.endDate)}`)
    : '';
  const presetLabel = PRESET_LABELS[period.preset];
  return presetLabel ? `${presetLabel} (${rangeLabel})` : (rangeLabel || 'All dates');
}

function AgingBucketBadge({ bucket }) {
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-md border text-xs font-medium ${BUCKET_STYLES[bucket] || 'bg-ink-canvas text-ink-body border-ink-line'}`}>
      {BUCKET_LABELS[bucket] || bucket}
    </span>
  );
}

function exportCSV(rows) {
  const headers = ['Waybill No', 'Waybill Date', 'Client/Consignee', 'Route/Details', 'Reference No.', 'Seal No.', 'Aging Bucket', 'Pull-Out Date', 'Age (Pull-Out)', 'Amount'];
  const csvRows = rows.map((row) => [
    row.waybillNo || '',
    formatDate(row.waybillDate),
    row.consignee || '',
    row.route || '',
    row.referenceNumber || '',
    row.sealNo || '',
    BUCKET_LABELS[row.agingBucket] || row.agingBucket || '',
    row.pullOutDate ? formatDate(row.pullOutDate) : '—',
    row.ageDays ?? '—',
    PHP(row.amount || 0),
  ]);
  const csv = [headers, ...csvRows]
    .map((record) => record.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(','))
    .join('\r\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = 'undelivered-freight-forwarding-report.csv';
  link.click();
  URL.revokeObjectURL(url);
}

export default function UndeliveredFreightForwardingReportPage() {
  const [period, setPeriod] = useState({ preset: 'YTD', ...computePeriodRange('YTD') });
  const [consignee, setConsignee] = useState('');
  const [wbNo, setWbNo] = useState('');
  const [referenceNo, setReferenceNo] = useState('');
  const [sealNo, setSealNo] = useState('');
  const [minDays, setMinDays] = useState('');
  const [pullOutStatus, setPullOutStatus] = useState('all');
  const [data, setData] = useState(emptyData);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [bulkDate, setBulkDate] = useState('');
  const [bulkError, setBulkError] = useState('');
  const [bulkResult, setBulkResult] = useState(null);
  // The filters actually behind the currently-loaded `data` — text/number
  // filters only take effect on "Apply filters", so live wbNo/consignee/etc.
  // state can be ahead of what's on screen. Print and export must reflect
  // what's displayed, so they read this instead of the raw input state.
  const [appliedFilters, setAppliedFilters] = useState({ wbNo: '', consignee: '', referenceNo: '', sealNo: '', minDays: '' });

  // Filter overrides let callers (e.g. Reset) pass fresh values straight
  // through instead of relying on state that hasn't committed yet.
  const loadReport = async (overrides = {}) => {
    setLoading(true);
    setSelected(new Set());
    setBulkResult(null);
    try {
      const p = { ...period, ...overrides };
      const f = {
        wbNo:          overrides.wbNo          !== undefined ? overrides.wbNo          : wbNo,
        consignee:     overrides.consignee     !== undefined ? overrides.consignee     : consignee,
        referenceNo:   overrides.referenceNo   !== undefined ? overrides.referenceNo   : referenceNo,
        sealNo:        overrides.sealNo        !== undefined ? overrides.sealNo        : sealNo,
        minDays:       overrides.minDays       !== undefined ? overrides.minDays       : minDays,
        pullOutStatus: overrides.pullOutStatus !== undefined ? overrides.pullOutStatus : pullOutStatus,
      };
      setAppliedFilters({ wbNo: f.wbNo, consignee: f.consignee, referenceNo: f.referenceNo, sealNo: f.sealNo, minDays: f.minDays });
      const res = await waybillAPI.getUndeliveredFreightForwarding({
        dateFrom: p.startDate,
        dateTo: p.endDate,
        pullOutStatus: f.pullOutStatus,
        ...(f.wbNo.trim()        ? { wbNo: f.wbNo.trim() }               : {}),
        ...(f.consignee.trim()   ? { consignee: f.consignee.trim() }     : {}),
        ...(f.referenceNo.trim() ? { referenceNo: f.referenceNo.trim() } : {}),
        ...(f.sealNo.trim()      ? { sealNo: f.sealNo.trim() }           : {}),
        ...(f.minDays !== ''     ? { minDays: f.minDays }                : {}),
      });
      setData(res.data?.data || emptyData);
      setLoaded(true);
    } catch (e) {
      console.error(e);
      setData(emptyData);
      setLoaded(true);
    } finally {
      setLoading(false);
    }
  };

  // Auto-refetch on period change, per the shared ReportToolbar convention —
  // the bespoke text/number filters below use an explicit "Apply" instead,
  // since those shouldn't re-query on every keystroke.
  useEffect(() => {
    loadReport({ ...period });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period.startDate, period.endDate]);

  const handleReset = () => {
    const defaultPeriod = { preset: 'YTD', ...computePeriodRange('YTD') };
    setConsignee('');
    setWbNo('');
    setReferenceNo('');
    setSealNo('');
    setMinDays('');
    setPullOutStatus('all');
    setPeriod(defaultPeriod);
    loadReport({ ...defaultPeriod, wbNo: '', consignee: '', referenceNo: '', sealNo: '', minDays: '', pullOutStatus: 'all' });
  };

  const handlePullOutStatusChange = (status) => {
    setPullOutStatus(status);
    loadReport({ pullOutStatus: status });
  };

  const handlePrint = () => {
    const params = new URLSearchParams({
      dateFrom: period.startDate,
      dateTo: period.endDate,
      preset: period.preset,
      pullOutStatus,
      ...(period.preset === 'FY' ? { fiscalYear: String(new Date(period.startDate).getFullYear()) } : {}),
      ...(appliedFilters.wbNo.trim()        ? { wbNo: appliedFilters.wbNo.trim() }               : {}),
      ...(appliedFilters.consignee.trim()   ? { consignee: appliedFilters.consignee.trim() }     : {}),
      ...(appliedFilters.referenceNo.trim() ? { referenceNo: appliedFilters.referenceNo.trim() } : {}),
      ...(appliedFilters.sealNo.trim()      ? { sealNo: appliedFilters.sealNo.trim() }           : {}),
      ...(appliedFilters.minDays !== ''     ? { minDays: appliedFilters.minDays }                : {}),
    });
    window.open(`/billing/reports/print/undelivered-freight-forwarding?${params}`, '_blank');
  };

  const summaryByBucket = useMemo(() => {
    const map = {};
    (data.agingSummary || []).forEach((s) => { map[s.bucket] = s.count; });
    return map;
  }, [data.agingSummary]);

  const pullOutSummaryByBucket = useMemo(() => {
    const map = {};
    (data.pullOutAgingSummary || []).forEach((s) => { map[s.bucket] = s; });
    return map;
  }, [data.pullOutAgingSummary]);

  const toggleSelected = (waybillId) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(waybillId)) next.delete(waybillId);
      else next.add(waybillId);
      return next;
    });
  };

  const allRowIds = (data.rows || []).map((r) => r.waybillId);
  const allSelected = allRowIds.length > 0 && allRowIds.every((id) => selected.has(id));
  const toggleSelectAll = () => {
    setSelected(allSelected ? new Set() : new Set(allRowIds));
  };

  const { mutate: applyBulkPullOutDate, isPending: bulkSaving } = useGuardedMutation(async () => {
    setBulkError('');
    setBulkResult(null);
    if (selected.size === 0) { setBulkError('Select at least one waybill.'); return; }
    if (!bulkDate) { setBulkError('Choose a pull-out date.'); return; }
    try {
      const res = await waybillAPI.bulkSetPullOutDate({
        waybillIds: [...selected],
        pullOutDate: bulkDate,
      });
      setBulkResult(res.data?.data || null);
      await loadReport();
    } catch (e) {
      setBulkError(e?.response?.data?.message || 'Failed to set pull-out date.');
      throw e; // keep the idempotency key for retry
    }
  });

  return (
    <Layout pageTitle="Undelivered Freight Forwarding">
      <style>{`
        @media print {
          .no-print { display: none !important; }
          .print-header { display: block !important; }
          @page { size: A4 landscape; margin: 10mm; }
          table { border-collapse: collapse; width: 100%; }
          th, td { border: 1px solid #999; padding: 3px 6px; font-size: 9pt; }
          thead { background: #f3f4f6 !important; }
        }
        .print-header { display: none; }
      `}</style>

      <div className="print-header mb-3">
        <p className="font-bold text-lg">UNDELIVERED FREIGHT FORWARDING REPORT</p>
        <p className="text-sm">Waybill date: {periodDisplayLabel(period)}</p>
      </div>

      <div className="no-print space-y-4">
        <ReportToolbar
          showPeriod
          period={period}
          onPeriodChange={setPeriod}
          onExportCSV={() => exportCSV(data.rows || [])}
          onPrint={handlePrint}
          onRefresh={() => loadReport()}
          refreshing={loading}
          extra={(
            <>
              <input
                type="text"
                value={consignee}
                onChange={(e) => setConsignee(e.target.value)}
                placeholder="Consignee / client"
                className="border border-ink-line rounded-md px-3 py-2 text-sm text-ink w-44 focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
              />
              <input
                type="text"
                value={wbNo}
                onChange={(e) => setWbNo(e.target.value)}
                placeholder="Waybill no."
                className="border border-ink-line rounded-md px-3 py-2 text-sm text-ink w-36 focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
              />
              <input
                type="text"
                value={referenceNo}
                onChange={(e) => setReferenceNo(e.target.value)}
                placeholder="Reference no."
                className="border border-ink-line rounded-md px-3 py-2 text-sm text-ink w-36 focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
              />
              <input
                type="text"
                value={sealNo}
                onChange={(e) => setSealNo(e.target.value)}
                placeholder="Seal no."
                className="border border-ink-line rounded-md px-3 py-2 text-sm text-ink w-32 focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
              />
              <input
                type="number"
                min="0"
                value={minDays}
                onChange={(e) => setMinDays(e.target.value)}
                placeholder="Min. age (pull-out)"
                className="border border-ink-line rounded-md px-3 py-2 text-sm text-ink w-32 focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
              />
              <Button variant="outline" size="sm" onClick={() => loadReport()}>
                Apply filters
              </Button>
              <Button variant="ghost" size="sm" onClick={handleReset}>
                Reset filters
              </Button>
            </>
          )}
        />

        <div className="flex items-center gap-2">
          <span className="text-xs font-medium text-ink-body uppercase tracking-wide">Pull-Out Date:</span>
          <div className="inline-flex rounded-lg border border-ink-line overflow-hidden">
            {['all', 'with', 'without'].map((status) => (
              <button
                key={status}
                type="button"
                onClick={() => handlePullOutStatusChange(status)}
                className={`px-3 py-1.5 text-sm font-medium transition-colors ${
                  pullOutStatus === status
                    ? 'bg-primary-600 text-white'
                    : 'bg-white text-ink-body hover:bg-ink-canvas'
                }`}
              >
                {PULLOUT_STATUS_LABELS[status]}
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {['0-7', '8-15', '16-30', '31+'].map((bucket) => (
            <div key={bucket} className={`rounded-xl border p-3 ${BUCKET_STYLES[bucket]}`}>
              <p className="text-xs font-medium opacity-80">{BUCKET_LABELS[bucket]}</p>
              <p className="text-lg font-medium">{summaryByBucket[bucket] || 0}</p>
            </div>
          ))}
        </div>
        <p className="text-xs text-ink-body">
          Amber = needs attention (16–30 days undelivered) · Red = overdue (31+ days undelivered)
        </p>

        <div>
          <h3 className="text-xs font-semibold text-ink-body uppercase tracking-wide mb-2">Pull-Out Date Aging</h3>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
            {['0-7', '8-15', '16-30', '31+'].map((bucket) => (
              <div key={bucket} className={`rounded-xl border p-3 ${BUCKET_STYLES[bucket]}`}>
                <p className="text-xs font-medium opacity-80">{BUCKET_LABELS[bucket]}</p>
                <p className="text-lg font-medium">{pullOutSummaryByBucket[bucket]?.count || 0}</p>
                <p className="text-xs opacity-80">₱{PHP(pullOutSummaryByBucket[bucket]?.amount || 0)}</p>
              </div>
            ))}
            <div className="rounded-xl border p-3 bg-accent-50 text-accent-700 border-accent-200">
              <p className="text-xs font-medium opacity-80">No Pull-Out Date</p>
              <p className="text-lg font-medium">{data.noPullOutGroup?.count || 0}</p>
              <p className="text-xs opacity-80">₱{PHP(data.noPullOutGroup?.amount || 0)}</p>
            </div>
          </div>
        </div>
      </div>

      {pullOutStatus === 'without' && (
        <div className="no-print bg-white rounded-xl border border-ink-line p-4 mt-4 space-y-3">
          <h3 className="text-xs font-semibold text-ink-body uppercase tracking-wide">Bulk-Set Pull-Out Date</h3>
          <p className="text-xs text-ink-body">Select waybills below, choose a date, and apply it to all of them at once.</p>
          <div className="flex items-center gap-3 flex-wrap">
            <span className="text-sm text-ink">{selected.size} selected</span>
            <input
              type="date"
              value={bulkDate}
              onChange={(e) => setBulkDate(e.target.value)}
              className="border border-ink-line rounded-md px-3 py-2 text-sm text-ink focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
            />
            <Button
              variant="primary"
              size="sm"
              disabled={bulkSaving || selected.size === 0 || !bulkDate}
              onClick={applyBulkPullOutDate}
            >
              {bulkSaving ? 'Applying…' : 'Apply to Selected'}
            </Button>
          </div>
          {bulkError && <p className="text-sm text-danger-700">{bulkError}</p>}
          {bulkResult && (
            <p className="text-sm text-success-700">
              Updated {bulkResult.updated}{bulkResult.skipped ? `, skipped ${bulkResult.skipped}` : ''}.
            </p>
          )}
        </div>
      )}

      <div className="overflow-x-auto bg-white rounded-xl border border-ink-line mt-4">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="bg-ink-canvas">
              {pullOutStatus === 'without' && (
                <th className="px-2 py-2 text-left">
                  <input type="checkbox" checked={allSelected} onChange={toggleSelectAll} className="w-4 h-4 accent-primary-500" />
                </th>
              )}
              <th className="px-2 py-2 text-left text-xs tracking-wide text-ink-body uppercase">Waybill No.</th>
              <th className="px-2 py-2 text-left text-xs tracking-wide text-ink-body uppercase">Waybill Date</th>
              <th className="px-2 py-2 text-left text-xs tracking-wide text-ink-body uppercase">Client/Consignee</th>
              <th className="px-2 py-2 text-left text-xs tracking-wide text-ink-body uppercase">Route/Details</th>
              <th className="px-2 py-2 text-left text-xs tracking-wide text-ink-body uppercase">Reference No.</th>
              <th className="px-2 py-2 text-left text-xs tracking-wide text-ink-body uppercase">Seal No.</th>
              <th className="px-2 py-2 text-center text-xs tracking-wide text-ink-body uppercase">Aging Bucket</th>
              <th className="px-2 py-2 text-left text-xs tracking-wide text-ink-body uppercase">Pull-Out Date</th>
              <th className="px-2 py-2 text-right text-xs tracking-wide text-ink-body uppercase">Age (Pull-Out)</th>
              <th className="px-2 py-2 text-right text-xs tracking-wide text-ink-body uppercase">Amount</th>
            </tr>
          </thead>
          <tbody>
            {!loaded || (data.rows || []).length === 0 ? (
              <tr>
                <td colSpan={pullOutStatus === 'without' ? 11 : 10} className="px-2 py-6 text-center text-ink-body">
                  {loading ? 'Loading…' : 'No undelivered freight forwarding waybills found.'}
                </td>
              </tr>
            ) : (
              (data.rows || []).map((r) => (
                <tr key={r.waybillId} className="border-t border-ink-line hover:bg-ink-canvas">
                  {pullOutStatus === 'without' && (
                    <td className="px-2 py-1.5">
                      <input
                        type="checkbox"
                        checked={selected.has(r.waybillId)}
                        onChange={() => toggleSelected(r.waybillId)}
                        className="w-4 h-4 accent-primary-500"
                      />
                    </td>
                  )}
                  <td className="px-2 py-1.5">{r.waybillNo}</td>
                  <td className="px-2 py-1.5">{formatDate(r.waybillDate)}</td>
                  <td className="px-2 py-1.5">{r.consignee}</td>
                  <td className="px-2 py-1.5">{r.route}</td>
                  <td className="px-2 py-1.5">{r.referenceNumber || '—'}</td>
                  <td className="px-2 py-1.5">{r.sealNo || '—'}</td>
                  <td className="px-2 py-1.5 text-center"><AgingBucketBadge bucket={r.agingBucket} /></td>
                  <td className="px-2 py-1.5">{r.pullOutDate ? formatDate(r.pullOutDate) : '—'}</td>
                  <td className="px-2 py-1.5 text-right">{r.ageDays ?? '—'}</td>
                  <td className="px-2 py-1.5 text-right">₱{PHP(r.amount || 0)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </Layout>
  );
}
